import { CandidateProfile, Gender, UserProfile, UserRole } from '../models/user-profile.model';
import { AuthenticationResponseDto } from '../models/auth.model';
import { CandidateProfileDto, CandidateProfileUpdateRequestDto } from '../models/candidate-profile.model';

/**
 * Auth response → domenski modeli. Čiste funkcije (zrcali job.mapper.ts).
 *
 * `detectGender` je izdvojen iz AuthService da bude jedan izvor istine —
 * koristi ga i ovaj mapper i AuthService (signup/updateProfile).
 */

/** Auto-detect gender from Croatian first name endings. */
export function detectGender(name: string): Gender {
  const first = name.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  if (!first) return 'male';
  // Croatian female names typically end in -a, -ica, -ka, -na
  const femaleEndings = ['a', 'ica', 'ka'];
  // Exceptions: common male names ending in -a
  const maleExceptions = ['luka', 'nikola', 'matija', 'andrija', 'ilija', 'josipa', 'nikša', 'saša'];
  if (maleExceptions.includes(first)) return 'male';
  if (femaleEndings.some(e => first.endsWith(e))) return 'female';
  return 'male';
}

/**
 * Backend rola (string) → UserRole, 1:1 mapping.
 * Nepoznata rola → employer (zadržava dosadašnji fallback).
 */
export function mapBackendRole(role: string): UserRole {
  switch (role) {
    case 'CANDIDATE': return 'candidate';
    case 'USER':      return 'employer';
    case 'ADMIN':     return 'admin';
    case 'SUBADMIN':  return 'subadmin';
    default:          return 'employer';
  }
}

/**
 * AuthenticationResponse → CandidateProfile.
 *
 * `name` iz dto.fullName; `email`/`phone` iz argumenata (form input — auth
 * response ih ne sadrži). `role` preko mapBackendRole; `gender` preko
 * detectGender. Profilna polja koja auth response ne zna (experience/
 * education/licenses/availability) ostaju prazna — pun profil je kasniji
 * task (`loadFullProfile` chain u `auth.service.ts` ih rehidrira iz baze).
 *
 * `phone` je 3. pozicijski optional dodan za OTP login flow — postojeći
 * callers (email/Google/Facebook/signup) prosljeđuju samo email i ne diraju se.
 */
export function mapAuthResponseToProfile(
  dto: AuthenticationResponseDto,
  email: string = '',
  phone: string = '',
): UserProfile {
  const role = mapBackendRole(dto.role);
  const base = { name: dto.fullName, email, phone, gender: detectGender(dto.fullName) };
  if (role === 'candidate') {
    return { ...base, role, experience: '', education: '', licenses: [], availability: '' };
  } else {
    return { ...base, role, companyId: dto.companyId, companyName: dto.companyName };
  }
}

/**
 * Split full name na firstname/lastname po LAST spaceu (hrvatska konvencija:
 * surname je tipično jedna riječ, first name može biti više riječi).
 * Single-word input → cijeli string je firstname, lastname prazan.
 */
export function splitName(name: string): { firstname: string; lastname: string } {
  const trimmed = name.trim();
  if (!trimmed) return { firstname: '', lastname: '' };
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace === -1) return { firstname: trimmed, lastname: '' };
  return {
    firstname: trimmed.slice(0, lastSpace).trim(),
    lastname: trimmed.slice(lastSpace + 1).trim(),
  };
}

/**
 * CandidateProfileDto → CandidateProfile.
 *
 * `name` kombinira firstname + lastname. Gender re-derivira iz firstname-a.
 * `role` dolazi izvana (u JWT-u je, ne u response DTO-u). CV polja
 * (hasCv/cvFileName/cvUploadedAt) sada dolaze direktno iz DTO-a — backend
 * vraća pun CV snapshot na GET i PUT /my-profile.
 */
export function mapCandidateProfileDtoToProfile(
  dto: CandidateProfileDto,
  role: 'candidate',
): CandidateProfile {
  return {
    role,
    name: `${dto.firstname} ${dto.lastname}`.trim(),
    email: dto.email,
    phone: dto.phone,
    street: dto.street,
    city: dto.city,
    gender: detectGender(dto.firstname),
    experience: dto.experience,
    education: dto.education,
    licenses: dto.licenses,
    availability: dto.availability,
    hasCv: dto.hasCv,
    cvFileName: dto.cvFileName,
    cvUploadedAt: dto.cvUploadedAt,
    phoneVerified: dto.phoneVerified,
    emailVerified: dto.emailVerified,
    authProvider: dto.authProvider,
    hasPassword: dto.hasPassword,
    totalApplications: dto.totalApplications,
  };
}

/**
 * CandidateProfile → request body za PUT /my-profile.
 *
 * Splita `name` na firstname/lastname. NE šalje email (backend ga
 * ignorira), gender (derived), role/cvFileName (nisu u update DTO-u).
 */
export function mapDomainToUpdateRequest(
  profile: CandidateProfile,
): CandidateProfileUpdateRequestDto {
  const { firstname, lastname } = splitName(profile.name);
  return {
    firstname,
    lastname,
    phone: profile.phone,
    street: profile.street,
    city: profile.city,
    experience: profile.experience,
    education: profile.education,
    licenses: profile.licenses,
    availability: profile.availability,
  };
}
